import {
  type ISocketOrderEvent,
  type TSocketOrderEventKey
} from '@/types/ISocketOrderEvent';
import { type IOrderListItem } from '@/types/IOrder';
import { isRecord } from './isRecord';

const KNOWN_EVENT_KEYS: ReadonlySet<string> = new Set(['order_post_persist']);

const isKnownEventKey = (value: unknown): value is TSocketOrderEventKey =>
  typeof value === 'string' && KNOWN_EVENT_KEYS.has(value);

/**
 * Проверяет, что объект похож на заказ по ключевым полям. Глубокую валидацию
 * намеренно не делаем — сверяем только то, по чему дальше работает логика.
 *
 * Заказ опознаётся **только по `uuid`** (PAR-5189). Раньше guard требовал ещё и
 * числовой `id` — и на бэкенде `par-5087`, где `id` из события пропал,
 * отбрасывал бы каждое событие: ни тоста, ни подсветки, ни звука, и ни одной
 * ошибки в консоли. Требовать поле, по которому ничего не решается, — ровно то,
 * от чего эта проверка и должна быть мелкой.
 */
const isOrderLike = (value: unknown): value is IOrderListItem =>
  isRecord(value) && typeof value.uuid === 'string';

/**
 * Type guard формы события заказа из Mercure. Сверяет обязательные поля
 * обёртки и форму `data` (как заказ). Используется при парсинге SSE-потока и
 * при чтении персистнутой истории уведомлений из localStorage.
 *
 * @param value - произвольное значение (распарсенный JSON)
 * @returns `true`, если значение соответствует форме `ISocketOrderEvent`
 */
export const isSocketOrderEvent = (
  value: unknown
): value is ISocketOrderEvent =>
  isRecord(value) &&
  isKnownEventKey(value.event) &&
  typeof value.status === 'string' &&
  typeof value.datetime === 'number' &&
  isOrderLike(value.data);
